import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { formatCustomDate } from "../../utils/formatters";
import UserModal from "./modal";
import UserActivityModal from "./userActivityModal";

export default function Users() {
  const [currentRow, setCurrentRow] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openActivityLog, setOpenActivityLog] = useState(false);

  const columns = [
    {
      field: "profile_picture",
      headerName: "Avatar",
      width: 70,
      type: "actions",
      renderCell: (value) => (
        <Box>
          <Avatar
            src={`${process.env.REACT_APP_STORAGE_URL}/${value.row.profile_picture}`}
            alt={`${value.row.full_name.slice(0)}`}
            sx={{ width: 24, height: 24 }}
          />
        </Box>
      ),
    },
    { field: "id", headerName: "ID", width: 70, type: "number" },
    { field: "full_name", headerName: "Name", width: 500 },
    {
      field: "roles",
      headerName: "Roles",
      width: 100,
      renderCell: (value) => {
        const roles = value.row.roles.map((role) => role.name);
        return roles.join(", ");
      },
      sortable: false,
    },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "date_registered",
      headerName: "Date Registered",
      width: 140,
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.date_registered),
      type: "date",
    },
    {
      field: "email_verified_at",
      headerName: "Email verified at",
      width: 140,
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.email_verified_at),
      type: "date",
    },
    {
      field: "phone_number",
      headerName: "Phone Number",
      width: 150,
      renderCell: (value) => <div>{value.row.phone_number || "N/A"}</div>,
    },
    {
      filed: "",
      headerName: "Activity Log",
      width: 100,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("centers-view") && (
            <IconButton
              color="info"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenActivityLog(true);
              }}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
    {
      field: "is_active",
      headerName: "Is Active?",
      width: 100,
      type: "boolean",
    },
    {
      field: "",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("user-edit") && (
            <Tooltip title="Edit user info">
              <IconButton
                color="info"
                onClick={() => {
                  setCurrentRow(value.row);
                  setOpenEdit(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {checkPermission("user-edit") && (
            <>
              {value.row.is_active ? (
                <Tooltip title="Deactivate user">
                  <IconButton
                    color="error"
                    onClick={() => {
                      setCurrentRow(value.row);
                      setOpenConfirm(true);
                      setIsActivating(false);
                    }}
                  >
                    <DoDisturbOnIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Activate user">
                  <IconButton
                    color="success"
                    onClick={() => {
                      setCurrentRow(value.row);
                      setOpenConfirm(true);
                      setIsActivating(true);
                    }}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {checkPermission("user-delete") && (
            <Tooltip title="Delete user">
              <IconButton
                color="error"
                onClick={() => {
                  setCurrentRow(value.row);
                  setIsDeleting(true);
                  setOpenConfirm(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const handleModalClose = () => {
    setOpenConfirm(false);
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenActivityLog(false);
    setCurrentRow(null);
  };

  const handleUserSuspend = async () => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/users/${currentRow.id}`,
        {
          is_active: currentRow.is_active ? 0 : 1,
          avatar_index: currentRow.avatar_index,
        },
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        setCurrentRow(null);
        handleModalClose();
        swal("User suspended successfully.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to suspend user. Please try again.", {
        icon: "error",
      });
    }
  };

  const handleUserAdd = async (values) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/users`,
        values,
      );

      if (response.status === 201) {
        setRefresh(!refresh);
        handleModalClose();
        swal("User created successfully.", { icon: "success" });
      }
    } catch (error) {
      swal("User creation failed.", { icon: "error" });
    }
  };

  const handleUserEdit = async (values, id) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_URL}/users/${id}`,
        values,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        swal("User updated successfully.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update user. Please try again.", {
        icon: "error",
      });
    }
  };

  const handleUserDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/users/${currentRow.id}`,
      );

      if (response.status === 204) {
        setRefresh(!refresh);
        handleModalClose();
        setIsDeleting(false);
        swal("User deleted successfully.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete user. Please try again.", {
        icon: "error",
      });
    }
  };

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Users</Typography>

          {checkPermission("centers-create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add new
            </Button>
          )}
        </Box>

        <CustomTable
          columns={columns}
          fetchDataUrl="users/paginated"
          refresh={refresh}
        />
      </MainLayout>

      {openAdd && (
        <UserModal
          open={openAdd}
          onClose={handleModalClose}
          onSubmit={handleUserAdd}
        />
      )}

      {currentRow && openEdit && (
        <UserModal
          open={openEdit}
          onClose={handleModalClose}
          onSubmit={handleUserEdit}
          user={currentRow}
        />
      )}

      {currentRow && openConfirm && !isDeleting && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleUserSuspend}
          title={`Are you sure you want to ${isActivating ? "activate" : "deactivate"} this user account?`}
          message={
            <span>
              <strong>{currentRow.full_name}</strong> will
              {!isActivating && " not"} be able to login after this action.
            </span>
          }
        />
      )}

      {openConfirm && isDeleting && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleUserDelete}
          title={`Are you sure you want to delete this user account?`}
          message={
            <span>
              The user account <strong>{currentRow.full_name}</strong> will be
              delete. Note that this action is not reversible.
            </span>
          }
        />
      )}

      {openActivityLog && (
        <UserActivityModal
          open={openActivityLog}
          onClose={handleModalClose}
          userId={currentRow.id}
        />
      )}
    </>
  );
}
